import { listLegacyChecklistMedia, recoverLegacyChecklistMedia } from '@/services/sync/LegacyChecklistMediaRecovery';
import { reviewChecklistCancellations, confirmChecklistCancellation } from '@/services/sync/ChecklistCancellationRuntime';
import { globalState } from '@/state/globalState';
import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Linking,
  Platform,
  Alert,
  ActivityIndicator
} from 'react-native';
import { observer } from '@legendapp/state/react';
import { useRouter } from 'expo-router';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SettingsSupportLogger } from '../../../../utils/logger';
import theme from '../../../../config/theme';
import { APP_CONSTANTS } from '../../../../config/constants';
import { useWebAlert } from '../../../../components/WebAlert';
import {
  getSettingsContentPaddingBottom,
  getSettingsHeaderPaddingTop,
} from '@/utils/safeArea';
import { isIOS } from '@/utils/platform';
import { authorizeSupportReset, performControlledSupportReset, sendSupportDiagnosticExplicitly, verifySupportSynchronization } from '@/services/diagnostics/supportDiagnosticRuntime';
import { sendSupportFullStateExplicitly } from '@/services/diagnostics/supportFullStateRuntime';

// Logger para a página de suporte
const logger = SettingsSupportLogger.Main;

// Interface para os itens do FAQ
interface FAQItem {
  id: string;
  question: string;
  answer: string;
  isExpanded: boolean;
}

const createFAQItems = (isIOSContactFlow: boolean): FAQItem[] => [
  ...(isIOSContactFlow
    ? [
        {
          id: '1',
          question: 'Como funciona meu acesso no iPhone?',
          answer:
            'No iPhone, o App Guincho funciona como aplicativo complementar para contas empresariais já habilitadas na plataforma. Depois do login, você usa os recursos liberados para sua empresa.',
          isExpanded: false
        },
        {
          id: '2',
          question: 'Minha conta deveria estar ativa. O que faço?',
          answer:
            'Se você acredita que sua empresa já deveria ter acesso ativo neste aparelho, fale com o suporte para conferirmos o cadastro e a liberação da conta.',
          isExpanded: false
        },
        {
          id: '3',
          question: 'Como falar com o suporte?',
          answer:
            'Use os botões desta tela ou o atalho Ajuda na navegação do app para falar com nossa equipe de suporte.',
          isExpanded: false
        },
      ]
    : [
        {
          id: '1',
          question: 'Como posso alterar meu plano?',
          answer:
            'Para alterar seu plano, acesse a seção "Assinatura" nas configurações e clique em "Alterar plano". Você verá as opções disponíveis e poderá escolher a que melhor atende suas necessidades.',
          isExpanded: false
        },
        {
          id: '2',
          question: 'Como funciona o período de teste?',
          answer:
            'O período de teste dura 7 dias e começa no momento em que você se inscreve. Durante esse período, você terá acesso a todos os recursos premium. Ao final do período, será cobrada a assinatura escolhida, a menos que você cancele antes.',
          isExpanded: false
        },
        {
          id: '3',
          question: 'Como cancelar minha assinatura?',
          answer:
            'Para cancelar sua assinatura, acesse a seção "Assinatura" nas configurações e clique em "Cancelar assinatura". O cancelamento será efetivado ao final do período já pago.',
          isExpanded: false
        },
      ]),
  {
    id: '4',
    question: 'Posso usar o aplicativo offline?',
    answer: 'Sim, o aplicativo foi projetado para funcionar offline. Os dados são sincronizados automaticamente quando você tem conexão com a internet.',
    isExpanded: false
  },
  {
    id: '5',
    question: 'Como recuperar minha senha?',
    answer: 'Na tela de login, clique em "Esqueceu sua senha?" e siga as instruções para redefinir sua senha.',
    isExpanded: false
  }
];

// Componente para cada item do FAQ
const FAQItemComponent = ({ 
  item, 
  onToggle 
}: { 
  item: FAQItem, 
  onToggle: (id: string) => void 
}) => {
  return (
    <View style={styles.faqItem}>
      <TouchableOpacity 
        style={styles.faqQuestion}
        onPress={() => onToggle(item.id)}
      >
        <Text style={styles.faqQuestionText}>{item.question}</Text>
        <MaterialCommunityIcons 
          name={item.isExpanded ? 'chevron-up' : 'chevron-down'} 
          size={20} 
          color={theme.colors.text.secondary} 
        />
      </TouchableOpacity>
      
      {item.isExpanded && (
        <View style={styles.faqAnswer}>
          <Text style={styles.faqAnswerText}>{item.answer}</Text>
        </View>
      )}
    </View>
  );
};

// Página principal de suporte
const SupportScreen = observer(() => {
  const router = useRouter();
  const safeAreaInsets = useSafeAreaInsets();
  const headerPaddingTop = getSettingsHeaderPaddingTop(safeAreaInsets.top);
  const scrollViewBottomPadding = getSettingsContentPaddingBottom(safeAreaInsets.bottom);
  const { showAlert } = useWebAlert();
  const [isResetting, setIsResetting] = useState(false);
  const [isSendingDiagnostic, setIsSendingDiagnostic] = useState(false);
  const [isSendingFullState, setIsSendingFullState] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResetAvailable, setIsResetAvailable] = useState(false);
  const isIOSContactFlow = isIOS;
  
  // Estado para o FAQ
  const [faqItems, setFaqItems] = useState<FAQItem[]>(() => createFAQItems(isIOSContactFlow));

  useEffect(() => {
    setFaqItems(createFAQItems(isIOSContactFlow));
  }, [isIOSContactFlow]);
  
  useEffect(() => {
    logger.debug('Página de suporte inicializada');
    void authorizeSupportReset().then(() => setIsResetAvailable(true)).catch(() => setIsResetAvailable(false));
    return () => {
      logger.debug('Página de suporte desmontada');
    };
  }, []);
  
  // Função para expandir/recolher um item do FAQ
  const toggleFAQItem = (id: string) => {
    setFaqItems(faqItems.map(item => 
      item.id === id 
        ? { ...item, isExpanded: !item.isExpanded } 
        : item
    ));
  };
  
  // Funções para ações de contato
  const handleEmail = () => {
    Linking.openURL('mailto:contato@horizonteinteligencia.com.br');
  };
  
  const handleWhatsApp = () => {
    Linking.openURL(APP_CONSTANTS.CONTACTS.WHATSAPP_URL);
  };
  
  const handleWebsite = () => {
    Linking.openURL('https://www.horizonteinteligencia.com.br');
  };

  const showMessage = (title: string, msg: string) => {
    if (Platform.OS !== 'android') {
      showAlert(title, msg, [{ text: 'OK' }]);
      return;
    }

    Alert.alert(title, msg);
  };

  const confirmReset = async (riskCount: number): Promise<boolean> => {
    const message = `Esta ação apaga todo o banco local e força uma sincronização completa. ${riskCount} pendência(s)/conflito(s) local(is) serão perdidos. A autorização será verificada novamente antes da limpeza.`;

    if (Platform.OS !== 'android') {
      return new Promise((resolve) => {
        showAlert(
          'Confirmar reinicialização',
          message,
          [
            { text: 'Cancelar', style: 'cancel', onPress: () => resolve(false) },
            { text: 'Continuar', style: 'destructive', onPress: () => resolve(true) }
          ]
        );
      });
    }

    return new Promise((resolve) => {
      Alert.alert(
        'Confirmar reinicialização',
        message,
        [
          { text: 'Cancelar', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Continuar', style: 'destructive', onPress: () => resolve(true) }
        ]
      );
    });
  };

  const [isReviewingCancellation, setIsReviewingCancellation] = useState(false);
  const handleReviewCancellation = async () => {
    if (isReviewingCancellation) return;
    setIsReviewingCancellation(true);
    try {
      const rows = await reviewChecklistCancellations();
      if (!rows.length) { showMessage('Cancelamentos', 'Nenhum par pendente de autorização nesta empresa.'); return; }
      for (const row of rows) {
        const manifest = JSON.parse(row.payload);
        const create = manifest.operations.find((op: any) => op.operation === 'create');
        const original = typeof create?.data === 'string' ? JSON.parse(create.data) : create?.data;
        const reference = original?.sequencial_codigo ? `${original.sequencial_codigo} — ` : '';
        const accepted = await new Promise<boolean>(resolve => {
          const buttons = [
            { text: 'Preservar', onPress: () => resolve(false) },
            { text: 'Autorizar cancelamento', onPress: () => resolve(true) },
          ];
          const message = `Checklist ${reference}${row.item_id}\nEste checklist tem uma criação e uma exclusão pendentes. Autorizar impede o envio futuro deste checklist se ele ainda não existir no servidor. As operações originais serão arquivadas após confirmação. Você autoriza como administrador desta empresa?`;
          if (Platform.OS === 'android') Alert.alert('Autorizar este cancelamento?', message, buttons, { cancelable: false });
          else showAlert('Autorizar este cancelamento?', message, buttons);
        });
        if (!accepted) continue;
        const receipt = await confirmChecklistCancellation(row);
        showMessage('Cancelamento', receipt.result === 'cancelled'
          ? 'Cancelamento confirmado. Operações originais arquivadas.'
          : 'Há dados deste checklist no servidor. A exclusão original foi preservada para sincronização pelo seu autor.');
      }
    } catch (error) { showMessage('Cancelamento preservado', error instanceof Error ? error.message : String(error)); }
    finally { setIsReviewingCancellation(false); }
  };

  const [isRecoveringMedia, setIsRecoveringMedia] = useState(false);
  const handleRecoverMedia = async () => {
    if (isRecoveringMedia) return;
    setIsRecoveringMedia(true);
    try {
      const rows = await listLegacyChecklistMedia();
      if (!rows.length) { showMessage('Mídias pendentes', 'Nenhuma mídia antiga pode ser recuperada com segurança nesta conta.'); return; }
      for (const row of rows) {
        const accepted = await new Promise<boolean>(resolve => {
          const buttons = [ { text: 'Preservar', onPress: () => resolve(false) },
            { text: 'Recuperar esta mídia', onPress: () => resolve(true) } ];
          const message = `Checklist ${row.item_id}\nCampo ${row.campo_id}\nArquivo ${row.local_uri.split('/').pop()}\nA conta que criou esta mídia não foi registrada. Você confirma o envio deste arquivo como uma nova recuperação na sua conta?`;
          if (Platform.OS === 'android') Alert.alert('Recuperar mídia antiga?', message, buttons, { cancelable: false });
          else showAlert('Recuperar mídia antiga?', message, buttons);
        });
        if (accepted) { await recoverLegacyChecklistMedia(row); showMessage('Mídia recuperada', 'O servidor confirmou o arquivo e a resposta do checklist.'); }
      }
    } catch (error) { showMessage('Mídia preservada', error instanceof Error ? error.message : String(error)); }
    finally { setIsRecoveringMedia(false); }
  };

  const handleSendDiagnostic = async () => {
    if (isSendingDiagnostic) return;
    setIsSendingDiagnostic(true);
    try {
      const receipt = await sendSupportDiagnosticExplicitly();
      logger.info(`[SUPPORT-DIAGNOSTIC] Envio concluído protocolo=${receipt.protocol}`);
      showMessage(
        'Diagnóstico enviado',
        `Protocolo ${receipt.protocol}. Informe este código ao suporte.`
      );
    } catch (error) {
      logger.error('[SUPPORT-DIAGNOSTIC] Falha no envio manual:', error);
      showMessage('Erro', 'Não foi possível enviar o diagnóstico. Tente novamente.');
    } finally {
      setIsSendingDiagnostic(false);
    }
  };

  const handleSendFullState = async () => {
    if (isSendingFullState) return;
    setIsSendingFullState(true);
    try {
      const receipt = await sendSupportFullStateExplicitly();
      showMessage('Dados enviados', `Protocolo ${receipt.protocol}. Informe este código ao suporte.`);
    } catch {
      logger.error('[SUPPORT-FULL-STATE] Falha no envio manual: support_full_state_send_failed');
      showMessage('Erro', 'Não foi possível enviar os dados completos. Confira sua conexão e tente novamente.');
    } finally {
      setIsSendingFullState(false);
    }
  };

  const handleVerifySynchronization = async () => {
    if (isVerifying) return;
    setIsVerifying(true);
    try {
      const result = await verifySupportSynchronization();
      showMessage('Verificação enviada', `Protocolo ${result.protocol}.`);
    } catch {
      showMessage('Não foi possível verificar', 'Confira sua conexão e tente novamente.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResetLocalData = async () => {
    if (isResetting) {
      return;
    }

    try {
      const { summary } = await authorizeSupportReset();
      const riskCount = Object.values(summary.pending_by_type).reduce((total, count) => total + count, 0) + summary.pending_images + summary.pending_deletions + Object.values(summary.conflicts_by_status).reduce((total, count) => total + count, 0);
      if (!await confirmReset(riskCount)) return;
      setIsResetting(true);
      await performControlledSupportReset();
      showMessage('Sucesso', 'Dados locais reiniciados e sincronização completa confirmada.');
    } catch (error) {
      setIsResetAvailable(false);
      const code = error instanceof Error ? error.message : '';
      if (code === 'database_reset_restart_required') {
        showMessage('Reabra o app', 'A limpeza local foi concluída. Reabra o app para carregar os dados novamente.');
      } else if (code === 'support_reset_sync_failed' || code === 'support_reset_reopen_failed') {
        showMessage('Sincronização incompleta', 'Os dados locais foram reiniciados, mas a reabertura não foi confirmada. Mantenha o app online e tente sincronizar novamente.');
      } else if (code === 'support_reset_clear_failed') {
        showMessage('Estado não confirmado', 'Não foi possível confirmar a limpeza local. Feche e abra o app antes de tentar novamente.');
      } else {
        showMessage('Ação indisponível', 'A autorização remota não pôde ser confirmada. Nenhum dado foi apagado.');
      }
    } finally {
      setIsResetting(false);
    }
  };
  
  // Navegação para voltar
  const handleGoBack = () => {
    router.back();
  };
  
  return (
    <View style={styles.container}>
      <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={handleGoBack}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Suporte</Text>
        <View style={styles.headerRight} />
      </View>
      
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollViewContent, { paddingBottom: scrollViewBottomPadding }]}
        showsVerticalScrollIndicator={false}
      >
        {/* Seção de contatos */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Entre em contato</Text>
          
          <TouchableOpacity 
            style={styles.contactItem}
            onPress={handleWhatsApp}
          >
            <MaterialCommunityIcons name="whatsapp" size={24} color="#25D366" />
            <Text style={styles.contactText}>{APP_CONSTANTS.CONTACTS.WHATSAPP_NUMBER.replace('55', '+').replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')}</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.contactItem}
            onPress={handleEmail}
          >
            <MaterialCommunityIcons name="email-outline" size={24} color={theme.colors.primary.default} />
            <Text style={styles.contactText}>contato@horizonteinteligencia.com.br</Text>
          </TouchableOpacity>
          
          {!isIOSContactFlow && (
            <TouchableOpacity 
              style={styles.contactItem}
              onPress={handleWebsite}
            >
              <MaterialCommunityIcons name="web" size={24} color={theme.colors.primary.default} />
              <Text style={styles.contactText}>www.horizonteinteligencia.com.br</Text>
            </TouchableOpacity>
          )}
        </View>
        
        {/* Seção de FAQ */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Perguntas frequentes</Text>
          
          {faqItems.map(item => (
            <FAQItemComponent 
              key={item.id} 
              item={item} 
              onToggle={toggleFAQItem} 
            />
          ))}
        </View>

        <View style={styles.section}>
            <Text style={styles.sectionTitle}>Diagnóstico</Text>
            {String(globalState.user.profile.get()?.user_type).toLowerCase() === 'admin' && (
              <TouchableOpacity style={styles.resetButton} onPress={handleReviewCancellation} disabled={isReviewingCancellation}>
                <Text style={styles.resetButtonText}>{isReviewingCancellation ? 'Revisando...' : 'Revisar cancelamentos pendentes'}</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity style={styles.resetButton} onPress={handleRecoverMedia} disabled={isRecoveringMedia}>
              <Text style={styles.resetButtonText}>{isRecoveringMedia ? 'Recuperando...' : 'Revisar mídias antigas pendentes'}</Text>
            </TouchableOpacity>
            <Text style={styles.diagnosticText}>
              Envie os logs recentes com os dados operacionais registrados pelo app. Credenciais técnicas continuam protegidas.
            </Text>
            <TouchableOpacity style={[styles.resetButton, isVerifying && styles.resetButtonDisabled]} onPress={handleVerifySynchronization} disabled={isVerifying}>
              {isVerifying ? <ActivityIndicator color={theme.colors.text.inverse} /> : <MaterialCommunityIcons name="sync-alert" size={18} color={theme.colors.text.inverse} />}
              <Text style={styles.resetButtonText}>{isVerifying ? 'Verificando...' : 'Verificar sincronização'}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.resetButton, isSendingDiagnostic && styles.resetButtonDisabled]}
              onPress={handleSendDiagnostic}
              disabled={isSendingDiagnostic}
            >
              {isSendingDiagnostic ? (
                <ActivityIndicator color={theme.colors.text.inverse} />
              ) : (
                <MaterialCommunityIcons
                  name="file-send-outline"
                  size={18}
                  color={theme.colors.text.inverse}
                />
              )}
              <Text style={styles.resetButtonText}>
                {isSendingDiagnostic ? 'Enviando...' : 'Enviar diagnóstico'}
              </Text>
            </TouchableOpacity>
            <Text style={styles.diagnosticText}>
              Quando solicitado pelo suporte, envie também os dados completos desta empresa neste aparelho, incluindo checklists, valores, pendências e mídias locais disponíveis.
            </Text>
            <TouchableOpacity
              style={[styles.resetButton, isSendingFullState && styles.resetButtonDisabled]}
              onPress={handleSendFullState}
              disabled={isSendingFullState}
            >
              {isSendingFullState ? <ActivityIndicator color={theme.colors.text.inverse} /> : <MaterialCommunityIcons name="database-export-outline" size={18} color={theme.colors.text.inverse} />}
              <Text style={styles.resetButtonText}>{isSendingFullState ? 'Enviando dados...' : 'Enviar dados completos ao suporte'}</Text>
            </TouchableOpacity>
          </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Reiniciar dados locais</Text>

          <View style={styles.resetInfoCard}>
            <MaterialCommunityIcons name="database-refresh" size={22} color={theme.colors.primary.default} />
            <View style={styles.resetInfoContent}>
              <Text style={styles.resetInfoTitle}>Correção rápida de sincronização</Text>
              <Text style={styles.resetInfoText}>
                Apaga o banco local do app e inicia uma nova sincronização completa. Você continua logado.
              </Text>
            </View>
          </View>

          <TouchableOpacity
            style={[styles.resetButton, (!isResetAvailable || isResetting) && styles.resetButtonDisabled]}
            onPress={handleResetLocalData}
            disabled={!isResetAvailable || isResetting}
          >
            {isResetting ? (
              <ActivityIndicator color={theme.colors.text.inverse} />
            ) : (
              <MaterialCommunityIcons name="sync" size={18} color={theme.colors.text.inverse} />
            )}
            <Text style={styles.resetButtonText}>
              {isResetting ? 'Reiniciando...' : 'Reiniciar sincronização'}
            </Text>
          </TouchableOpacity>
          {!isResetAvailable && <Text style={styles.diagnosticText}>Disponível somente quando autorizado para esta empresa e com conexão ativa.</Text>}
        </View>
        
        {/* Seção de horário de atendimento */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Horário de atendimento</Text>
          
          <Text style={styles.infoText}>
            Segunda a sexta-feira: 09:00 às 18:00
          </Text>
          <Text style={styles.infoText}>
            Sábado: 09:00 às 13:00
          </Text>
          <Text style={styles.infoText}>
            Domingo e feriados: fechado
          </Text>
        </View>
      </ScrollView>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  header: {
    backgroundColor: theme.colors.primary.default,
    padding: theme.spacing.lg,
    paddingTop: Platform.OS === 'ios' ? theme.spacing.xl + theme.spacing.sm : theme.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: theme.fontSizes.xl,
    fontWeight: theme.fontWeights.bold as any,
    color: theme.colors.text.inverse,
  },
  headerRight: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    paddingBottom: theme.spacing.xl,
  },
  section: {
    backgroundColor: theme.colors.background.primary,
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.lg,
    ...theme.shadows.md,
  },
  sectionTitle: {
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.md,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border.light,
  },
  contactText: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
    marginLeft: theme.spacing.md,
  },
  faqItem: {
    marginBottom: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    borderRadius: theme.borderRadius.sm,
    overflow: 'hidden',
  },
  faqQuestion: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.background.tertiary,
  },
  faqQuestionText: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
    color: theme.colors.text.primary,
    flex: 1,
    marginRight: theme.spacing.sm,
  },
  faqAnswer: {
    padding: theme.spacing.md,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border.light,
  },
  faqAnswerText: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
    lineHeight: 20,
  },
  infoText: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.sm,
  },
  diagnosticText: {
    fontSize: theme.fontSizes.md,
    lineHeight: 20,
    color: theme.colors.text.secondary,
    marginBottom: theme.spacing.md,
  },
  resetInfoCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: theme.colors.background.tertiary,
    borderRadius: theme.borderRadius.sm,
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
  },
  resetInfoContent: {
    flex: 1,
    marginLeft: theme.spacing.sm,
  },
  resetInfoTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.xs,
  },
  resetInfoText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    lineHeight: 18,
  },
  resetButton: {
    marginTop: theme.spacing.md,
    backgroundColor: theme.colors.primary.default,
    borderRadius: theme.borderRadius.sm,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  resetButtonDisabled: {
    opacity: 0.6,
  },
  resetButtonText: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.inverse,
    marginLeft: theme.spacing.sm,
  },
});

export default SupportScreen; 
